import React, { useState, useRef } from "react";
import "../../assets/css/nlis.css";
import { Button, Form } from "react-bootstrap";
import classNames from "classnames";
import CustomAlert from "../general/customAlert";
import CustomTable from "../general/CustomTable";
import { erpEnquiry } from "../../api/nlis";
import CircularProgress from "@mui/material/CircularProgress";

const columns = [
  { name: "Transaction ID", value: "transaction_id" },
  { name: "PIC", value: "pic" },
  { name: "Program Code", value: "program_code" },
  { name: "Status Code", value: "status_code" },
  { name: "Status Description", value: "status_description" },
  { name: "Valid", value: "valid" },
];

const ERPEnquiryPage = () => {
  const [picCode, setPicCode] = useState("");
  const [fetchedData, setFetchedData] = useState([]);
  const [isSendingRequest, setSendingRequest] = useState(false);
  const [isMsg, setIsMsg] = useState(false);
  const [msg, setMsg] = useState(null);
  const [isError, setError] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [visibleColumns, setVisibleColumns] = useState({});
  const abortControllerRef = useRef(null);

  const rowsPerPage = 20;
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;

  const handlePageChange = (page) => setCurrentPage(page);

  const handleCancel = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setSendingRequest(false);
    setError(true);
    setIsMsg(true);
    setMsg("Request cancelled.");
  };

  const sendSOAPRequest = async () => {
    if (!picCode || picCode.trim() === "") {
      setError(true);
      setIsMsg(true);
      setMsg("Please enter a PIC code before checking status.");
      return;
    }

    abortControllerRef.current = new AbortController();

    setSendingRequest(true);
    setError(false);
    setIsMsg(false);
    setFetchedData([]);

    try {
      const res = await erpEnquiry(
        { pic: picCode.trim() },
        abortControllerRef.current.signal
      );

      if (res.data && res.data.length > 0) {
        setFetchedData(res.data);
        setTotalPages(Math.ceil(res.data.length / rowsPerPage));
        setVisibleColumns(
          columns.reduce((acc, item) => {
            acc[item.name] = true;
            return acc;
          }, {})
        );
      } else {
        setError(true);
        setIsMsg(true);
        setMsg("No results found for this PIC code.");
      }
    } catch (err) {
      if (err.name === "CanceledError" || err.code === "ERR_CANCELED") {
        // already handled in handleCancel
        return;
      }
      console.error("[NLIS] ERP Enquiry error:", err);
      setError(true);
      setIsMsg(true);
      setMsg(err?.response?.data?.message || err?.response?.data?.error || "Error fetching ERP status.");
    } finally {
      setSendingRequest(false);
    }
  };

  return (
    <div className="nlis-page erp-enquiry">
      <h4 className="mb-3 text-center text-acumen-color">NLIS ERP Enquiry</h4>

      <div className="container-fluid">
        <div className="d-flex flex-column flex-md-row">

          {/* PIC Code input */}
          <div className="col-12 col-md-8 mb-1 mb-md-0 d-flex p-0">
            <Form.Group className="m-0 p-0 col-6">
              <Form.Label className="m-0">PIC Code</Form.Label>
              <input
                type="text"
                className="form-control m-0 w-100"
                placeholder="e.g."
                disabled={isSendingRequest}
                value={picCode}
                onChange={(e) => setPicCode(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendSOAPRequest()}
              />
            </Form.Group>
          </div>

          {/* Buttons */}
          <div className="col-12 col-md-4 d-flex justify-content-md-end mt-3 mt-md-0 align-items-end gap-2">
            <Button
              className={classNames("btn btn-acumen-color", {
                disabled: isSendingRequest,
              })}
              onClick={sendSOAPRequest}>
              {isSendingRequest ? "Checking..." : "Check Status"}
            </Button>
            {isSendingRequest && (
              <Button variant="outline-secondary" onClick={handleCancel}>
                Cancel
              </Button>
            )}
          </div>
        </div>

        {/* Loading animation */}
        {isSendingRequest && (
          <div className="d-flex flex-column align-items-center mt-4 gap-2">
            <CircularProgress style={{ color: "#6d4c41" }} />
            <span className="text-muted">
              Contacting NLIS — this may take up to 60 seconds...
            </span>
          </div>
        )}

        {/* Results table */}
        {!isSendingRequest && (
          <div className="mt-4">
            {fetchedData.length > 0 ? (
              <>
                <CustomTable
                  items={fetchedData.slice(startIndex, endIndex)}
                  columns={columns}
                  visibleColumns={visibleColumns}
                  setVisibleColumns={setVisibleColumns}
                  cantHideColumns={["pic"]}
                  maxHeight="calc(100vh - 320px)"
                  horizontalMargin="mx-1"
                />
                {totalPages > 1 && (
                  <div className="pagination mt-2">
                    <button
                      className="btn btn-acumen-color text-white me-2"
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange(currentPage - 1)}>
                      Previous
                    </button>
                    {Array.from({ length: totalPages }, (_, index) => (
                      <button
                        key={index + 1}
                        className={`btn ${
                          currentPage === index + 1
                            ? "btn-secondary"
                            : "btn-light"
                        } me-1`}
                        onClick={() => handlePageChange(index + 1)}>
                        {index + 1}
                      </button>
                    ))}
                    <button
                      className="btn btn-acumen-color text-white ms-2"
                      disabled={currentPage === totalPages}
                      onClick={() => handlePageChange(currentPage + 1)}>
                      Next
                    </button>
                  </div>
                )}
              </>
            ) : (
              !isError && (
                <div className="alert alert-warning mt-2">
                  Enter PIC code to get the status.
                </div>
              )
            )}
          </div>
        )}
      </div>

      {isMsg && (
        <CustomAlert
          isError={isError}
          message={msg}
          showMessage={isMsg}
          setShowMessage={setIsMsg}
        />
      )}
    </div>
  );
};

export default ERPEnquiryPage;